/**
 * <Tip> — small convenience wrapper around the Radix tooltip primitives.
 *
 * Usage:
 *   <Tip text="Refresh the queue"><Button>...</Button></Tip>
 *
 * Keeps three things consistent across the product:
 *   1. Always uses `asChild` so the trigger inherits existing styles.
 *   2. Pulls the global 500 ms delay from <TooltipProvider>.
 *   3. Centralises the className on the content so styling stays uniform.
 *
 * Tooltip copy convention: one plain-language sentence, no jargon, explains
 * what the element does (not what it IS). Source the text from foxchat_kb
 * description_auto whenever a matching row exists so the two stay in sync.
 */
import * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface TipProps {
  text: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  /** Skip rendering the tooltip (e.g., when the trigger is disabled and you don't want chatter). */
  disabled?: boolean;
}

export function Tip({ text, children, side = "top", className, disabled }: TipProps) {
  if (disabled || !text) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children as any}</TooltipTrigger>
      <TooltipContent side={side} className={cn("text-xs max-w-[240px]", className)}>
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
